import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import WebNotification from 'react-web-notification';
import NotificationSystem from 'react-notification-system';

import Footer from '../../Components/Footer/Footer';
import Auth from '../Authentication/Auth';
import Api from '../../Services/Api';

import OrderDetails from './OrderDetails';

const ONE_MINUTE = 60 * 1000;
const ONE_DAY = 24 * 60 * 60 * 1000;
const NEW_NOTIFICATION_SOUND = 'https://newt.phys.unsw.edu.au/music/bellplates/sounds/bellplate-corner3.mp3';
const COMPLETE_NOTIFICATION_SOUND = 'https://newt.phys.unsw.edu.au/music/bellplates/sounds/bellplate-10mm.mp3';

class OrdersPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      queryDate: new Date().getTime(),
      lastQueryDate: new Date(),
      firstFetch: true,
      orders: {
        open: [],
        fulfilled: [],
        closed: [],
        canceled: [],
      },
      scheduledFetch: null,
      notificationIgnore: true,
      notificationTitle: ''
    };

    this.prevDateButtonClicked = this.prevDateButtonClicked.bind(this);
    this.nextDateButtonClicked = this.nextDateButtonClicked.bind(this);
    this.notifyNewOrders = this.notifyNewOrders.bind(this);

    this.sendNotification = this.sendNotification.bind(this);
    this.handleNotSupported = this.handleNotSupported.bind(this);
    this.handlePermissionGranted = this.handlePermissionGranted.bind(this);
    this.handlePermissionDenied = this.handlePermissionDenied.bind(this);
    this.handleNotificationOnShow = this.handleNotificationOnShow.bind(this);
    this.handleNotificationOnClick = this.handleNotificationOnClick.bind(this);
    this.handleNotificationOnClose = this.handleNotificationOnClose.bind(this);
    this.handleNotificationOnError = this.handleNotificationOnError.bind(this);
  }

  componentWillMount() {
    this.fetchData(this);
    if (this.state.scheduledFetch !== null) {
      clearInterval(this.state.scheduledFetch);
    }
    this.setState({ scheduledFetch: setInterval(this.fetchData.bind(this), ONE_MINUTE) });
  }

  componentWillUnmount() {
    clearInterval(this.state.scheduledFetch);
    this.setState({ scheduledFetch: null });
  }

  formatDate(timestamp) {
    const monthNames = [
      'January', 'February', 'March',
      'April', 'May', 'June', 'July',
      'August', 'September', 'October',
      'November', 'December'
    ];

    const dayNames = [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday',
      'Thursday', 'Friday', 'Saturday'
    ];

    const date = new Date(timestamp);

    const dateNumber = date.getDate();
    const monthIndex = date.getMonth();
    const dayIndex = date.getDay();
    const year = date.getFullYear();

    return `${dayNames[dayIndex]}, ${monthNames[monthIndex]} ${dateNumber}, ${year}`;
  }

  prevDateButtonClicked() {
    this.setState({ queryDate: this.state.queryDate - ONE_DAY }, () => {
      this.fetchData();
    });
  }

  nextDateButtonClicked() {
    this.setState({ queryDate: this.state.queryDate + ONE_DAY }, () => {
      this.fetchData();
    });
  }

  fetchData() {
    const myHeaders = new Headers();
    const endPoint = `v1/fbos/${this.props.fboId}/orders?timestamp=${this.state.queryDate}`;

    myHeaders.append('X-Api-Key', Auth.getBearerToken());

    this.request = Api.get(endPoint, {
      headers: myHeaders,
    }, true).then((response) => {
      const orders = response.orders;

      const orderGroups = {
        open: [],
        fulfilled: [],
        closed: [],
        canceled: [],
      };

      orders.forEach((order) => {
        switch (order.status) {
          case 'todo':
            orderGroups.open.push(order);
            break;
          case 'in progress':
            orderGroups.open.push(order);
            break;
          case 'fulfilled':
            orderGroups.fulfilled.push(order);
            break;
          case 'closed':
            orderGroups.closed.push(order);
            break;
          case 'canceled':
            orderGroups.canceled.push(order);
            break;
          default:
            break;
        }
      });

      this.notifyNewOrders(orderGroups.open);
      this.notifyCompleteOrders(orderGroups.closed);
      this.setState({ orders: orderGroups, firstFetch: false, lastQueryDate: new Date() });
    });
  }

  notifyNewOrders(openOrders) {
    if (this.state.firstFetch) {
      return;
    }

    const newOrders = openOrders
      .filter(order => new Date(order.createdAt).getTime() >= this.state.lastQueryDate.getTime());

    if (newOrders) {
      for (let i = 0; i < newOrders.length; i += 1) {
        const order = newOrders[i];
        const notificationDetails = {
          title: `Order #${order.orderNumber}`,
          body: 'A new orders is ready',
          sound: NEW_NOTIFICATION_SOUND,
          tag: 'new',
        };
        this.sendNotification(order, notificationDetails);
      }
    }
  }

  notifyCompleteOrders(closedOrders) {
    if (this.state.firstFetch) {
      return;
    }

    const completeOrders = closedOrders
      .filter(order => new Date(order.closedAt).getTime() >= this.state.lastQueryDate.getTime());
    if (completeOrders) {
      for (let i = 0; i < completeOrders.length; i += 1) {
        const order = completeOrders[i];
        const notificationDetails = {
          title: `Order #${order.orderNumber}`,
          body: 'Order is complete',
          sound: COMPLETE_NOTIFICATION_SOUND,
          tag: 'closed',
        };
        this.sendNotification(order, notificationDetails);
      }
    }
  }

  handlePermissionGranted() {
    let notificationIgnore = false;
    // Make sure window.Notification is supported
    // Mobile Android doesn't support it. This tests the support.
    try {
      new window.Notification('');
    } catch (e) {
      if (e.name === 'TypeError') {
        notificationIgnore = true;
      }
    }
    this.setState({
      notificationIgnore
    });
  }
  handlePermissionDenied() {
    this.setState({
      notificationIgnore: true
    });
  }
  handleNotSupported() {
    this.setState({
      notificationIgnore: true
    });
  }

  handleNotificationOnClick(e, tag) {
    window.focus();
    const { history } = this.props;
    history.push(`/orders/${e.target.data}`);
  }

  handleNotificationOnError(e, tag) {
  }

  handleNotificationOnClose(e, tag) {
  }

  handleNotificationOnShow(e, tag) {
    const notificationType = tag.split('+')[0];

    if (notificationType === 'new') {
      this.playSound('newNotificationSound');
    } else if (notificationType === 'closed') {
      this.playSound('completeNotificationSound');
    }
  }

  playSound(soundId) {
    document.getElementById(soundId).play();
  }

  sendNotification(order, notificationDetails) {
    if (this.state.notificationIgnore) {
      return;
    }

    const icon = '/icon-72x72.png';

    const now = new Date();
    // Available options
    // See https://developer.mozilla.org/en-US/docs/Web/API/Notification/Notification
    const options = {
      tag: `${notificationDetails.tag}+${now}`,
      icon,
      body: notificationDetails.body,
      lang: 'en',
      data: order.id,
      sound: notificationDetails.sound,
    };

    this.setState({ notificationTitle: notificationDetails.title, options });
  }

  renderOrderDetail(order) {
    return (<OrderDetails
      key={order.orderNumber}
      fboId={this.props.fboId}
      order={order}
      refs={() => ({ notificationSystem: this.notificationSystem })}
      history={this.props.history}
      location={this.props.location}
    />);
  }

  renderOpenOrders() {
    if (this.state.orders.open.length > 0) {
      return this.state.orders.open.map(order =>
        this.renderOrderDetail(order));
    }
    return (<article className="no-order-item">No Open Orders</article>);
  }

  renderFulfilledOrders() {
    if (this.state.orders.fulfilled.length > 0) {
      return this.state.orders.fulfilled.map(order =>
        this.renderOrderDetail(order));
    }
    return (<article className="no-order-item">No Fulfilled Orders</article>);
  }

  renderClosedOrders() {
    if (this.state.orders.closed.length > 0) {
      return this.state.orders.closed.map(order =>
        this.renderOrderDetail(order));
    }
    return (<article className="no-order-item">No Closed Orders</article>);
  }

  renderCanceledOrders() {
    if (this.state.orders.canceled.length > 0) {
      return this.state.orders.canceled.map(order =>
        this.renderOrderDetail(order));
    }
    return (<article className="no-order-item">No Cancelled Orders</article>);
  }

  renderOrderGroupHeadings() {
    return (
      <div className="order-item-headings">
        <div className="order-id">Order ID</div>
        <div className="customer-name">Customer Name</div>
        <div className="tail-number">Tail Number</div>
        <div className="summary">Summary</div>
        <div className="status">Status</div>
        <div className="action" />
        <div className="clear" />
      </div>
    );
  }

  render() {
    return (
      <div className='app'>
        <NotificationSystem ref={node => this.notificationSystem = node} />
        <WebNotification
          ignore={this.state.notificationIgnore && this.state.notificationTitle !== ''}
          notSupported={this.handleNotSupported}
          onPermissionGranted={this.handlePermissionGranted}
          onPermissionDenied={this.handlePermissionDenied}
          onShow={this.handleNotificationOnShow}
          onClick={this.handleNotificationOnClick}
          onClose={this.handleNotificationOnClose}
          onError={this.handleNotificationOnError}
          timeout={5000}
          title={this.state.notificationTitle}
          options={this.state.options}
        />
        <audio id="newNotificationSound" preload="auto">
          <track kind="captions" />
          <source src={NEW_NOTIFICATION_SOUND} type="audio/mpeg" />
          <embed hidden="true" autostart="false" loop="false" src={NEW_NOTIFICATION_SOUND} />
        </audio>
        <audio id="completeNotificationSound" preload="auto">
          <track kind="captions" />
          <source src={COMPLETE_NOTIFICATION_SOUND} type="audio/mpeg" />
          <embed hidden="true" autostart="false" loop="false" src={COMPLETE_NOTIFICATION_SOUND} />
        </audio>
        <main className="page-content">
          {this.props.children}
          <div className="orders-page-header">
            <h1>Orders</h1>
            <div className="date-navigation">
              <button className="prev" onClick={this.prevDateButtonClicked}> &#60; </button>
              <button
                className="next"
                onClick={this.nextDateButtonClicked}
                disabled={new Date().getTime() <= this.state.queryDate + ONE_DAY}
              >
                &#62;
              </button>
            </div>
            <h3>{this.formatDate(this.state.queryDate)}</h3>
            <Link to="/orders/create" className="btn btn-medium btn-primary right"
              params={{ fboId: this.props.fboId }}>
              <span className="mobile">+</span><span className="desktop">New Order</span>
            </Link>
          </div>
          {new Date().getTime() - ONE_DAY <= this.state.queryDate &&
            <div>
              <section className="order-group">
                <h4>Open <span className="order-count">{this.state.orders.open.length}</span></h4>
                {this.renderOrderGroupHeadings()}
                {this.renderOpenOrders()}
              </section>

              <section className="order-group">
                <h4>Fulfilled <span className="order-count">{this.state.orders.fulfilled.length}</span></h4>
                {this.renderOrderGroupHeadings()}
                {this.renderFulfilledOrders()}
              </section>
            </div>
          }

          <section className="order-group">
            <h4>Closed <span className="order-count">{this.state.orders.closed.length}</span></h4>
            {this.renderOrderGroupHeadings()}
            {this.renderClosedOrders()}
          </section>

          <section className="order-group">
            <h4>Cancelled <span className="order-count">{this.state.orders.canceled.length}</span></h4>
            {this.renderOrderGroupHeadings()}
            {this.renderCanceledOrders()}
          </section>
          <Footer />
        </main>
      </div>
    );
  }
}


OrdersPage.contextTypes = {
  fboId: PropTypes.number.isRequired,
};

OrdersPage.propTypes = {
  history: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({}).isRequired,
};

export default OrdersPage;




// WEBPACK FOOTER //
// ./src/Containers/Orders/OrdersPage.js